import type * as MonacoApi from 'monaco-editor/esm/vs/editor/editor.api.js';

import {COLOR_THEME_PRESETS} from '@core/constants/colorThemePresets';
import {createAllPresetMonacoDefinitionsFromColorPresets} from '@core/utils/buildMonacoThemeFromColorPreset';
import {
  mergePresetMonacoDefinition,
  type MonacoPresetThemeOverrides,
  type CustomMonacoEditorThemeId,
  type PresetMonacoDefinition,
  type PresetMonacoThemeId,
} from './mergeMonacoPresetTheme';

type TokenRule = MonacoApi.editor.ITokenThemeRule;

type MonacoEditorModule =
  typeof import('monaco-editor/esm/vs/editor/editor.api.js');

/**
 * Monaco preset themes: one light + one dark per entry in {@link COLOR_THEME_PRESETS},
 * using each preset’s primary / secondary / accent / background / status colors.
 */
export const PRESET_MONACO_THEME_DEFINITIONS: readonly PresetMonacoDefinition[] =
  createAllPresetMonacoDefinitionsFromColorPresets(COLOR_THEME_PRESETS);

/**
 * Canonical saved editor themes in style.json (not palette-preset ids).
 * Cloned from the first light/dark palette preset definitions as structural templates.
 */
export const CUSTOM_MONACO_EDITOR_DEFINITIONS: readonly PresetMonacoDefinition[] =
  (() => {
    // Product invariant: exactly one light (`vs`) and one dark (`vs-dark`) definition from presets.
    const light = PRESET_MONACO_THEME_DEFINITIONS.find(d => d.base === 'vs');
    const dark = PRESET_MONACO_THEME_DEFINITIONS.find(
      d => d.base === 'vs-dark',
    );
    if (!light || !dark) {
      if (import.meta.env.DEV) {
        console.warn(
          '[monacoPresetThemeDefinitions] Missing vs and/or vs-dark preset; custom-light/custom-dark editor themes are unavailable.',
        );
      }
      return [];
    }
    return [
      {...light, id: 'custom-light'},
      {...dark, id: 'custom-dark'},
    ];
  })();

export function findPresetMonacoDefinitionById(
  id: string,
): PresetMonacoDefinition | undefined {
  return PRESET_MONACO_THEME_DEFINITIONS.find(d => d.id === id);
}

export function findMonacoEditorDefinitionById(
  id: string,
): PresetMonacoDefinition | undefined {
  if (id === 'custom-light' || id === 'custom-dark') {
    return CUSTOM_MONACO_EDITOR_DEFINITIONS.find(d => d.id === id);
  }
  return findPresetMonacoDefinitionById(id);
}

export function isPresetMonacoThemeId(id: string): id is PresetMonacoThemeId {
  return PRESET_MONACO_THEME_DEFINITIONS.some(d => d.id === id);
}

export function isCustomMonacoEditorThemeId(
  id: string,
): id is CustomMonacoEditorThemeId {
  return id === 'custom-light' || id === 'custom-dark';
}

export function registerPresetMonacoThemes(
  monacoApi: MonacoEditorModule,
  overrides?: MonacoPresetThemeOverrides | null,
): void {
  const allDefs = [
    ...PRESET_MONACO_THEME_DEFINITIONS,
    ...CUSTOM_MONACO_EDITOR_DEFINITIONS,
  ];
  for (const def of allDefs) {
    const merged = mergePresetMonacoDefinition(
      def,
      overrides?.[def.id] ?? null,
    );
    monacoApi.editor.defineTheme(merged.id, {
      base: merged.base,
      inherit: true,
      rules: merged.rules as TokenRule[],
      colors: {
        'editor.background': merged.backgroundColor,
        'editor.foreground': merged.foregroundColor,
        ...merged.colors,
      },
    });
  }
}
